import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { VoiceSearchModal } from '../components/VoiceSearchModal';
import { useAuth } from '../hooks/useAuth';
import { useMonitors } from '../hooks/useMonitors';
import { useNotifications } from '../hooks/useNotifications';
import { useTranslation } from '../hooks/useTranslation';
import { api } from '../lib/api-client';
import { Card, Button, AlertBadge, Spinner, useToast } from '../components/ui';
import { MonitorCard } from '../components/MonitorCard';
import { PushNotificationPrompt } from '../components/PushNotificationPrompt';

function timeAgo(
  date: Date | string,
  t: (key: any) => string,
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const suffix = t('dashboard.timeAgoSuffix');
  if (diffMin < 1) return t('dashboard.timeJustNow');
  if (diffMin < 60) return `${diffMin} ${t('dashboard.timeMinAgo')}`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) {
    const unit = diffH === 1 ? t('dashboard.timeHourAgo') : t('dashboard.timeHoursAgo');
    return `${diffH} ${unit} ${suffix}`;
  }
  const diffD = Math.floor(diffH / 24);
  const unit = diffD === 1 ? t('dashboard.timeDayAgo') : t('dashboard.timeDaysAgo');
  return `${diffD} ${unit} ${suffix}`;
}

function EmptyState() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      {/* CSS illustration: plane icon */}
      <div className="mb-6 w-24 h-24 rounded-full bg-primary-50 flex items-center justify-center">
        <svg
          className="w-12 h-12 text-primary-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
          />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-text-primary mb-2">
        {t('dashboard.noMonitors')}
      </h2>
      <p className="text-text-secondary mb-6 max-w-md">
        {t('dashboard.noMonitorsAction')}
      </p>
      <Link to="/onboarding">
        <Button size="lg">{t('dashboard.addMonitor')}</Button>
      </Link>
    </div>
  );
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <Card padding="md" className="flex-1 min-w-0">
      <p className="text-sm text-text-secondary mb-1 truncate">{label}</p>
      <p
        className={`text-2xl font-bold truncate ${
          accent ? 'text-green-600' : 'text-text-primary'
        }`}
      >
        {value}
      </p>
    </Card>
  );
}

function InviteWidget() {
  const { t } = useTranslation();
  const [totalInvites, setTotalInvites] = useState<number | null>(null);

  useEffect(() => {
    api
      .get<{ code: string; inviteUrl: string; stats: { totalInvites: number } }>(
        '/invite/my-code',
      )
      .then((data) => setTotalInvites(data.stats.totalInvites))
      .catch(() => {});
  }, []);

  // Don't show if loaded and already at 5+ invites, or still loading
  if (totalInvites === null || totalInvites >= 5) return null;

  const progress = totalInvites % 5;

  return (
    <Card padding="md" className="flex-1 min-w-0">
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
          <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.25-9.75h16.5" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary mb-1">
            {t('dashboard.inviteFriendsProgress').replace('{progress}', String(progress))}
          </p>
          <div className="h-2 w-full rounded-full bg-surface-secondary overflow-hidden mb-2">
            <div
              className="h-full rounded-full bg-primary-600 transition-all duration-500"
              style={{ width: `${(progress / 5) * 100}%` }}
            />
          </div>
          <Link
            to="/settings?tab=invita"
            className="text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            {t('dashboard.shareYourLink')} &rarr;
          </Link>
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const {
    monitors,
    stats,
    isLoading: monitorsLoading,
    error: monitorsError,
    toggleActive,
    deleteMonitor,
    refetch: refetchMonitors,
  } = useMonitors();
  const {
    notifications,
    isLoading: notifsLoading,
    error: notifsError,
  } = useNotifications();

  useEffect(() => {
    const checkoutStatus = searchParams.get('checkout');
    if (checkoutStatus === 'success') {
      refreshUser();
      showToast(t('dashboard.checkoutSuccess'), 'success');
      setSearchParams({}, { replace: true });
    } else if (checkoutStatus === 'cancelled') {
      showToast(t('dashboard.checkoutCancelled'), 'info');
      setSearchParams({}, { replace: true });
    }
  }, []);

  const isLoading = monitorsLoading || notifsLoading;
  const error = monitorsError || notifsError;

  const userName = user?.name || user?.email?.split('@')[0] || t('dashboard.fallbackUsername');
  const recentNotifications = notifications.slice(0, 5);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <Button variant="outline" onClick={() => refetchMonitors()}>
          {t('common.tryAgain')}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
      {/* Push notification prompt */}
      <PushNotificationPrompt />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">
          {t('dashboard.welcome')}, {userName}!
        </h1>
        <Link to="/onboarding">
          <Button
            iconLeft={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            }
          >
            {t('dashboard.addMonitor')}
          </Button>
        </Link>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="flex gap-3 sm:gap-4 mb-8 overflow-x-auto">
          <StatCard
            label={t('dashboard.activeMonitors')}
            value={String(stats?.monitoraggiAttivi ?? 0)}
          />
          <StatCard
            label={t('dashboard.totalSavings')}
            value={`${(stats?.risparmioStimato ?? 0).toFixed(0)}\u20AC`}
            accent
          />
          <StatCard
            label={t('notifications.unread')}
            value={String(stats?.notificheNonLette ?? 0)}
          />
        </div>
      )}

      {/* Invite widget */}
      <div className="mb-8">
        <InviteWidget />
      </div>

      {/* Monitors list or empty state */}
      {monitors.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            {t('dashboard.yourMonitors')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {monitors.map((monitor) => (
              <MonitorCard
                key={monitor.id}
                monitor={monitor}
                onToggleActive={toggleActive}
                onDelete={deleteMonitor}
              />
            ))}
          </div>
        </>
      )}

      {/* Recent notifications */}
      {recentNotifications.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-text-primary">
              {t('dashboard.recentAlerts')}
            </h2>
            <Link
              to="/notifications"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('dashboard.seeAll')}
            </Link>
          </div>
          <Card padding="none">
            <ul className="divide-y divide-border">
              {recentNotifications.map((notif) => (
                <li key={notif.id}>
                  <Link
                    to="/notifications"
                    className={`flex items-start gap-3 px-4 py-3 hover:bg-surface-secondary transition-colors ${
                      !notif.letta ? 'bg-primary-50/50' : ''
                    }`}
                  >
                    <div className="shrink-0 mt-0.5">
                      <AlertBadge level={notif.tipo} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-sm truncate ${
                          !notif.letta ? 'font-semibold text-text-primary' : 'text-text-secondary'
                        }`}
                      >
                        {notif.titolo}
                      </p>
                      <p className="text-xs text-text-secondary truncate">
                        {notif.corpo}
                      </p>
                    </div>
                    <span className="text-xs text-text-secondary shrink-0 whitespace-nowrap">
                      {notif.inviataAt ? timeAgo(notif.inviataAt, t) : ''}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        </section>
      )}

      {/* Voice Search FAB — only for Pro and Unlimited users */}
      {user?.plan !== 'free' && (
        <>
          <button
            onClick={() => setVoiceOpen(true)}
            className="fixed bottom-24 md:bottom-8 right-4 z-40 h-14 w-14 rounded-full bg-primary-600 text-white shadow-lg flex items-center justify-center hover:bg-primary-700 active:scale-95 transition-all cursor-pointer"
            aria-label={t('dashboard.voiceSearchAria')}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
            </svg>
          </button>
          <VoiceSearchModal open={voiceOpen} onClose={() => setVoiceOpen(false)} />
        </>
      )}
    </div>
  );
}
